import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { usePsychologistContext } from "../../contexts/PsychologistContext";
import { useWidth } from "../../hooks/useWidth";
import Avatar from "../common/Avatar";
import NotificationBell from "../common/NotificationBell";
import { clearCompleteSession } from "../../hooks/api";
import { useGoogleAuth } from "../../hooks/api/auth/useGoogleAuth";
import logoPsi from "../../assets/logoPsiMammoliti.svg";
import apiCoreAdapter from "../../hooks/apiCoreAdapter";
import { hasValidSession } from "../../hooks/api";

interface MainLayoutProps {
  children: React.ReactNode;
  navigation?: React.ReactNode;
  onProfileClick?: () => void;
  showProfileButton?: boolean;
  noPadding?: boolean;
  isCalendarTourRunning?: boolean;
  /** Override del `background-color` blanco de `.admin-content` — usado por
   * vistas con fondo propio de punta a punta (ej. Home) para que no se vea
   * blanco por debajo/alrededor del contenido si su alto no calza exacto
   * con el de `<main>`. */
  contentBackground?: string;
}

export const MainLayout = ({
  children,
  navigation,
  onProfileClick,
  showProfileButton = true,
  noPadding = false,
  isCalendarTourRunning = false,
  contentBackground,
}: MainLayoutProps) => {
  const navigate = useNavigate();
  const { signOut: googleSignOut } = useGoogleAuth();
  const { user } = useAuth();
  const { clearPsychologist } = usePsychologistContext();
  const { size } = useWidth();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    // Asegurar que el body no quede bloqueado por flujos anteriores
    try {
      document.body.style.overflow = '';
      document.body.style.height = '';
    } catch { /* noop */ }
    return () => undefined;
  }, []);

  // Cargar foto de perfil (si existe) al montar
  useEffect(() => {
    // Inicializar con la foto de Google si ya está disponible (evita parpadeo)
    try {
      const googlePic = JSON.parse(localStorage.getItem('google_user') || '{}')?.picture;
      if (googlePic) setProfilePhoto(googlePic);
    } catch { /* noop */ }

    let cancelled = false;
    (async () => {
      try {
        if (!hasValidSession()) {
          setProfilePhoto(null);
          return;
        }
        const resp = await apiCoreAdapter.psychologistProfile();
        if (cancelled) return;
        const url = (resp as unknown as { profile_photo?: string | null })?.profile_photo ?? null;
        if (url && typeof url === 'string' && url.trim().length > 0) {
          setProfilePhoto(url);
        } else {
          // Fallback: foto de Google Auth
          try {
            const googlePic = JSON.parse(localStorage.getItem('google_user') || '{}')?.picture;
            if (!cancelled) setProfilePhoto(googlePic || null);
          } catch { if (!cancelled) setProfilePhoto(null); }
        }
      } catch {
        // noop
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resolveDriveUrl = (original?: string | null): string | undefined => {
    if (!original) return undefined;
    try {
      const d1 = original.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
      const d2 = original.match(/[?&]id=([^&]+)/i);
      const fileId = (d1 && d1[1]) || (d2 && d2[1]);
      if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
      return original;
    } catch {
      return original || undefined;
    }
  };

  const handleLogout = async () => {
    await googleSignOut();
    clearCompleteSession();
    clearPsychologist();
    navigate('/login');
  };

  const getContentMaxWidth = () => {
    if (size === 'l') return '100%';
    if (size === 'm') return '1400px';
    return '100%';
  };

  return (
    <div className="admin-panel-modern" style={{ 
      minHeight: '100vh', 
      overflow: 'visible', 
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      <header className="admin-header-modern" style={{
        flexShrink: 0,
        padding: '0 2rem',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative'
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
          <img src={logoPsi} alt="Psi Mammoliti" width={36} height={36} />
          <span className="header-brand-text">Psi Platform</span>
        </div>

        {/* Nav tabs — absolutely centered on the viewport (not on the space between the
            logo and profile/logout blocks, which differ in width and would otherwise
            throw the nav off-center), desktop only */}
        {navigation && (
          <nav
            className="admin-tabs header-tabs-desktop absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            id="header-tabs"
          >
            {navigation}
          </nav>
        )}

        {/* User + logout */}
        <style>{`
          .header-brand-text { font-size: 15px; font-weight: 700; color: #ffffff; font-family: 'DM Sans', system-ui, sans-serif; letter-spacing: -0.01em; }
          .header-profile-trigger { display: flex !important; align-items: center !important; gap: 0.5rem !important; background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2) !important; border-radius: 9999px !important; padding: 0.3rem 0.75rem 0.3rem 0.3rem !important; cursor: pointer !important; text-align: left !important; transition: background 0.2s !important; }
          .header-profile-trigger:hover { background: rgba(255,255,255,0.18) !important; }
          .header-name-text { font-size: 14px !important; font-weight: 600 !important; color: #ffffff !important; font-family: 'DM Sans', system-ui, sans-serif !important; }
          .header-right-section { display: flex !important; align-items: center !important; gap: 0.75rem !important; flex-shrink: 0 !important; }
          .logout-button { display: flex !important; align-items: center !important; background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2) !important; border-radius: 9999px !important; padding: 0.45rem !important; cursor: pointer !important; transition: background 0.2s !important; color: #ffffff !important; }
          .logout-button:hover { background: rgba(255,255,255,0.2) !important; }
          @media (max-width: 1023px) {
            .header-brand-text { display: none; }
            .header-tabs-desktop { display: none !important; }
            .header-name-text { display: none !important; }
          }
        `}</style>
        <div className="header-right-section">
          {showProfileButton && <NotificationBell />}
          {showProfileButton && (
            <button
              type="button"
              className="header-profile-trigger"
              onClick={onProfileClick}
              aria-label="open-profile-tab"
              id="header-profile-trigger"
              disabled={isCalendarTourRunning}
              style={{
                cursor: isCalendarTourRunning ? 'not-allowed' : 'pointer',
                opacity: isCalendarTourRunning ? 0.5 : 1,
                pointerEvents: isCalendarTourRunning ? 'none' : 'auto'
              }}
            >
              {profilePhoto ? (
                <img
                  src={resolveDriveUrl(profilePhoto)}
                  alt={user?.name || ""}
                  style={{ width: '2rem', height: '2rem', borderRadius: '9999px', objectFit: 'cover', background: '#ede9fe' }}
                />
              ) : (
                <Avatar
                  name={user?.name || ""}
                  lastName={user?.surname || ""}
                  sizeRem={2}
                />
              )}
              <span className="header-name-text">
                {user?.name || ""}
              </span>
            </button>
          )}
          <button
            type="button"
            className="logout-button"
            onClick={() => setShowLogoutConfirm(true)}
            aria-label="logout"
            id="header-logout-button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      {/* Bottom navigation — mobile only */}
      {navigation && (
        <nav className="bottom-nav-mobile" id="header-tabs-mobile">
          {navigation}
        </nav>
      )}

      <main className="admin-content" style={{
        flex: 1,
        overflow: 'visible',
        minHeight: 0,
        maxWidth: getContentMaxWidth(),
        width: '100%',
        margin: size === 'l' ? '0' : '0 auto',
        padding: noPadding ? (size === 's' ? '0 0 64px 0' : '0') : undefined,
        paddingBottom: (!noPadding && size === 's') ? '64px' : undefined,
        ...(contentBackground ? { backgroundColor: contentBackground } : {}),
      }}>
        {children}
      </main>


      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', borderRadius: '1.5rem', padding: '2rem', maxWidth: 400, width: '90%', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151', marginBottom: '1rem' }}>Cerrar sesión</h3>
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: 1.5 }}>
              ¿Seguro que querés cerrar sesión?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                type="button"
                style={{ background: '#6D57BC', color: '#fff', border: 'none', borderRadius: 9999, padding: '0.75rem 1.25rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={{ background: '#fff', color: '#ef4444', border: '2px solid #ef4444', borderRadius: 9999, padding: '0.75rem 1.25rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => { setShowLogoutConfirm(false); handleLogout(); }}
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

